import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink as RouterNavLink, useLocation } from "react-router-dom";
import { Container } from "@components/ui/Container";
import { Button } from "@components/ui/Button";
import { navigation, company } from "@data/company";
import { useMediaQuery } from "@hooks/useMediaQuery";
import { useScrollLock } from "@hooks/useScrollLock";
import { Logo } from "./Logo";
import styles from "./Header.module.css";

const Chevron = () => (
  <svg
    className={styles.chevron}
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 4.5L6 7.5L9 4.5" />
  </svg>
);

const Burger = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden>
    {open ? (
      <path
        d="M6 6L18 18M6 18L18 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    ) : (
      <>
        <path d="M4 7H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    )}
  </svg>
);

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 960px)");

  useScrollLock(mobileOpen && !isDesktop);

  useEffect(() => {
    if (isDesktop && mobileOpen) setMobileOpen(false);
  }, [isDesktop, mobileOpen]);

  useEffect(() => {
    let ticking = false;
    const update = () => {
      setScrolled((prev) => {
        const next = window.scrollY > 8;
        return prev === next ? prev : next;
      });
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const focusableSelector =
      'a, button, [tabindex]:not([tabindex="-1"]), input, select, textarea';
    const getFocusables = () => {
      const menu = menuRef.current;
      if (!menu) return [] as HTMLElement[];
      return [...menu.querySelectorAll<HTMLElement>(focusableSelector)].filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null,
      );
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (e.key === "Tab") {
        const focusables = getFocusables();
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    menuRef.current?.focus({ preventScroll: true });
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  const handleMobileLinkClick = (to: string) => {
    if (to === location.pathname) setMobileOpen(false);
  };

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ""}`}>
      <Container>
        <div className={styles.inner}>
          <Link to="/" className={styles.brand} aria-label={`${company.name} - Inicio`}>
            <Logo size={38} />
            <span className={styles.brandText}>
              <span className={styles.brandTitle}>Pomilio &amp; Asociados</span>
              <span className={styles.brandTag}>Seguros</span>
            </span>
          </Link>

          <nav className={styles.nav} aria-label="Principal">
            {navigation.map((item) => (
              <div key={item.to} className={styles.navItem}>
                <RouterNavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
                  }
                  end={item.to === "/"}
                >
                  {item.label}
                  {item.children && <Chevron />}
                </RouterNavLink>
                {item.children && (
                  <div className={styles.dropdown} role="menu">
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className={styles.dropdownLink}
                        role="menuitem"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className={styles.actions}>
            <Button
              as="a"
              href={`https://wa.me/${company.whatsapp}`}
              target="_blank"
              rel="noopener"
              variant="cta"
              size="sm"
            >
              Cotizar
            </Button>
            <button
              ref={toggleRef}
              type="button"
              className={styles.toggle}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
            >
              <Burger open={mobileOpen} />
            </button>
          </div>
        </div>
      </Container>

      {mobileOpen &&
        createPortal(
          <>
            <div
              className={styles.mobileBackdrop}
              onClick={() => setMobileOpen(false)}
              aria-hidden
            />
            <div
              ref={menuRef}
              id="mobile-menu"
              className={styles.mobileMenu}
              role="dialog"
              aria-modal="true"
              aria-label="Menú móvil"
              tabIndex={-1}
            >
              <nav className={styles.mobileNav} aria-label="Navegación móvil">
                {navigation.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={styles.mobileLink}
                    onClick={() => handleMobileLinkClick(item.to)}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className={styles.mobileActions}>
                <Button
                  as="a"
                  href={`https://wa.me/${company.whatsapp}`}
                  target="_blank"
                  rel="noopener"
                  variant="cta"
                  fullWidth
                >
                  Cotizar por WhatsApp
                </Button>
                <Button as="a" href={`tel:${company.phone.replace(/\s/g, "")}`} variant="secondary" fullWidth>
                  Llamar {company.phone}
                </Button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </header>
  );
}
